/**
 * `/agents/tasks/$id` - first-class task detail + bijection row (Phase 3.C).
 *
 * Distinct from `/agents/verification/$taskId`, the canonical review door.
 * This page links into that door; it never redirects it away.
 */

import { createRoute } from "@tanstack/react-router";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DataTable,
  IntentBadge,
  PageHeader,
  TaskCommitsSection,
  TaskDodSection,
  TaskEvidenceArtifactSection,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi } from "@excellent/ui/api";
import {
  evidence,
  type EvidenceBundleReceiptDto,
  type EvidenceJudgmentRow,
  type EvidenceTaskDetailDto,
} from "@excellent/ui/api/contracts/evidence";
import { rootRoute } from "./__root";

const MUTED = "type-subtext text-muted-foreground";
const NUM = "tabular-nums";

type BijectionRow = {
  id: number;
  claim: string;
  commits: string;
  receipts: string;
  verdict: string;
  landed: string;
  landedSource: string;
  landedAt: string | null;
};

function shortSha(sha: string): string {
  return sha.length <= 9 ? sha : sha.slice(0, 9);
}

function landedIntent(
  state: string,
): "success" | "danger" | "warning" | "neutral" {
  if (state === "integrated") return "success";
  if (state === "stranded" || state === "stranded-legacy") return "danger";
  if (state === "target-retired" || state === "unattributed-branch") return "warning";
  return "neutral";
}

function latestVerdict(detail: EvidenceTaskDetailDto): string {
  const last = detail.judgments[detail.judgments.length - 1];
  return last?.verdict ?? detail.verdict;
}

function verdictIntent(verdict: string): "success" | "danger" | "warning" | "neutral" {
  if (verdict === "accepted" || verdict === "approved" || verdict === "accept") return "success";
  if (verdict === "rejected" || verdict === "reject") return "danger";
  if (verdict === "edited") return "warning";
  return "neutral";
}

function receiptSummary(receipts: EvidenceBundleReceiptDto[]): string {
  if (receipts.length === 0) return "0";
  const passing = receipts.filter((r) => r.exitCode === 0).length;
  const failing = receipts.length - passing;
  return `${receipts.length} (${passing} pass / ${failing} fail)`;
}

function bijectionRow(detail: EvidenceTaskDetailDto): BijectionRow {
  const shas = detail.commits.map((c) => shortSha(c.sha));
  return {
    id: detail.taskId,
    claim: detail.ownerLabel ?? "-",
    commits: shas.length ? shas.join(" ") : "-",
    receipts: receiptSummary(detail.receipts),
    verdict: latestVerdict(detail),
    landed: detail.landed.state,
    landedSource: detail.landed.source,
    landedAt: detail.landed.at,
  };
}

function BijectionTable({ detail }: { detail: EvidenceTaskDetailDto }) {
  const row = bijectionRow(detail);
  const columns: DataTableColumn<BijectionRow>[] = [
    {
      key: "claim",
      header: "Claim",
      render: (r) => <span className="type-body">{r.claim}</span>,
    },
    {
      key: "commits",
      header: "Commit",
      render: (r) => (
        <code
          className={`${NUM} type-subtext`}
          title={detail.commits.map((c) => c.sha).join("\n")}
        >
          {r.commits}
        </code>
      ),
    },
    {
      key: "receipts",
      header: "Receipts",
      render: (r) => <span className={`${NUM} type-body`}>{r.receipts}</span>,
    },
    {
      key: "verdict",
      header: "Verdict",
      render: (r) => (
        <IntentBadge
          intent={verdictIntent(r.verdict)}
        >
          {r.verdict}
        </IntentBadge>
      ),
    },
    {
      key: "landed",
      header: "Landed",
      render: (r) => (
        <span className="inline-flex flex-col gap-0.5">
          <IntentBadge intent={landedIntent(r.landed)}>{r.landed}</IntentBadge>
          <span className={MUTED}>
            {r.landedSource}
            {r.landedAt ? ` / ${formatRelativeTime(r.landedAt)}` : ""}
          </span>
        </span>
      ),
    },
  ];

  return (
    <div data-testid="task-bijection-row">
      <DataTable rows={[row]} columns={columns} rowKey={(r) => String(r.id)} wrapInCard={false} />
    </div>
  );
}

function JudgmentsTable({ judgments }: { judgments: EvidenceJudgmentRow[] }) {
  const columns: DataTableColumn<EvidenceJudgmentRow>[] = [
    {
      key: "verdict",
      header: "Verdict",
      render: (r) => (
        <IntentBadge intent={verdictIntent(r.verdict)}>
          {r.verdict}
        </IntentBadge>
      ),
    },
    {
      key: "verifierAgent",
      header: "Verifier",
      render: (r) => <code className="type-subtext">{r.verifierAgent ?? "-"}</code>,
    },
    {
      key: "note",
      header: "Note",
      render: (r) => (
        <span className="type-body line-clamp-2" title={r.note ?? ""}>
          {r.note?.trim() || "-"}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "When",
      render: (r) => <span className={MUTED}>{formatRelativeTime(r.createdAt)}</span>,
    },
  ];
  if (!judgments.length) {
    return <p className={MUTED}>No accept/reject verdict recorded for this task yet.</p>;
  }
  return (
    <DataTable
      rows={judgments}
      columns={columns}
      rowKey={(r, i) => `${r.createdAt}-${r.verifierAgent ?? "na"}-${i}`}
      wrapInCard={false}
    />
  );
}

function AgentsTaskDetailPage() {
  const { id: idParam } = agentsTaskDetailRoute.useParams();
  const taskId = Number(idParam);
  const idOk = Number.isInteger(taskId) && taskId > 0;
  const { data, error, isLoading } = useApi(
    evidence.get,
    idOk ? { params: { id: taskId } } : null,
  );

  if (!idOk) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-destructive">Invalid task id.</p>
      </div>
    );
  }

  return (
    <div className="px-6 py-8 space-y-6" data-testid="task-detail">
      <PageHeader
        title={data ? `#${data.taskId} ${data.title}` : `Task #${taskId}`}
        description={
          data
            ? `${data.projectName ?? data.projectSlug} / ${data.sectionSlug} / claim -> commit -> receipts -> verdict -> landed`
            : "Loading task trail..."
        }
        breadcrumbs={[
          { label: "Agents", href: "/agents" },
          { label: `Task #${taskId}` },
        ]}
        toolbar={
          <Button variant="outline" asChild>
            <Link href={`/agents/verification/${taskId}`}>Review door</Link>
          </Button>
        }
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading...</p>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="type-body text-destructive">
              {error instanceof Error ? error.message : String(error)}
            </p>
          </CardContent>
        </Card>
      ) : data ? (
        <>
          <Card>
            <CardHeader>
              <CardTitle>Bijection</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className={MUTED}>
                {"One row: claim -> commit sha(s) -> gate receipts -> verdict -> landed."}
                none-required is not integrated; stranded means commits that did not land.
                {data.integrationSha ? (
                  <>
                    {" "}
                    integration_sha <code className="type-subtext">{shortSha(data.integrationSha)}</code>
                  </>
                ) : null}
              </p>
              <BijectionTable detail={data} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Verdicts</CardTitle>
            </CardHeader>
            <CardContent>
              <JudgmentsTable judgments={data.judgments} />
            </CardContent>
          </Card>

          <TaskEvidenceArtifactSection taskId={taskId} />
          <TaskDodSection taskId={taskId} />
          <TaskCommitsSection taskId={taskId} />
        </>
      ) : null}
    </div>
  );
}

export const agentsTaskDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/tasks/$id",
  component: AgentsTaskDetailPage,
});
